import React, { useEffect, useRef, useState } from "react";
import { ColumnValueRendererProps } from "../table.data.interface";
import { Tooltip } from "@vibe/core";
import { MondayApiQueryInstance } from "../../../services/monday/monday-service-ts";
import logger from "../../../lib/logger";
import { UserAvatar } from "../../ui/UserAvatar";
import { cachedPeople, rememberPeople } from "./personCache";

const PersonColumn = ({ columnData, itemIds, isPreview }: ColumnValueRendererProps) => {
  const [personsAndTeams, setPersonsAndTeams] = useState<any[]>([]);
  const [error, setError] = useState(false);
  // Only the latest lookup may draw: an older one answering late would show (or hide) old people.
  const requestRef = useRef(0);

  const fetchPersonData = async (columnId: string, itemIds?: number[], personsAndTeams?: any[]) => {
    const request = ++requestRef.current;
    const isLatest = () => request === requestRef.current;
    try {
      let combinedData: any[] = [];
      if (!isPreview) {
        setError(false);

        // Fetch data for teams and persons
        const response = await MondayApiQueryInstance.getPersonColumnData(itemIds!, columnId);

        const text = response?.items[0]?.column_values[0]?.text;

        const personsAndTeamsArray = response?.items[0]?.column_values[0]?.persons_and_teams;

        const names = text.split(",").map((name: string) => name.trim());

        personsAndTeamsArray.forEach((item: any, index: number) => {
          if (names[index]) {
            item.name = names[index];
          }
        });

        const persons = personsAndTeamsArray.filter((item: any) => item.kind === "person");

        const teams = personsAndTeamsArray.filter((item: any) => item.kind === "team");

        const personsId = persons.map((person: any) => person.id);

        const personData = await MondayApiQueryInstance.getUsersByUserId(personsId);
        rememberPeople(personData ?? []);

        combinedData = [...personData, ...teams];
      } else {
        const persons = columnData?.persons_and_teams?.filter(
          (item: any) => item.kind === "person",
        );

        const teams = columnData?.persons_and_teams?.filter((item: any) => item.kind === "team");

        const personsId = persons?.map((person: any) => person.id);

        const personData = await MondayApiQueryInstance.getUsersByUserId(personsId!);

        combinedData = [...personData, ...(teams || [])];
      }

      if (isLatest()) setPersonsAndTeams(combinedData);
    } catch (error: any) {
      if (!isLatest()) return;
      setError(true);
      logger.error("Error fetching person column data", error);
    }
  };

  useEffect(() => {
    // Right after an edit the row lists who's in the column, with their kinds. If they're all
    // people already drawn once, show them now; the fetch below then confirms it. Bare ids are
    // never looked up, since a team can share an id with a user.
    const entries = columnData?.persons_and_teams;
    if (entries?.length && entries.every((entry) => entry.kind === "person")) {
      const known = cachedPeople(entries.map((entry) => entry.id));
      if (known) setPersonsAndTeams(known);
    }
    if (columnData?.value !== "Empty Column" && columnData?.value) {
      fetchPersonData(columnData?.columnId, itemIds as number[]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnData?.columnId, columnData?.value, itemIds]);

  if (error) {
    return null;
  }

  if (
    columnData.value === "Empty Column" ||
    !columnData.value ||
    error ||
    !personsAndTeams ||
    personsAndTeams.length === 0
  ) {
    return null;
  }

  const arrayOfPersonsAndTeams = personsAndTeams;
  const displayedItems = arrayOfPersonsAndTeams.slice(0, 3);
  const remainingPersonandTeams = arrayOfPersonsAndTeams.slice(3);

  return (
    <div className="flex font-semibold">
      {displayedItems.map((personAndTeam: any, index) => (
        <Tooltip content={personAndTeam.name} key={index}>
          <UserAvatar
            name={personAndTeam.name}
            photoUrl={personAndTeam.photo_thumb}
            size="md"
            className="-mx-1 border-2 border-[#f5f6f8] cursor-pointer"
          />
        </Tooltip>
      ))}
      {arrayOfPersonsAndTeams.length > 3 && (
        <Tooltip
          content={
            <div className="flex flex-col">
              {remainingPersonandTeams.map((personAndTeam: any, index) => (
                <div key={index} className="text-sm text-white">
                  {personAndTeam?.name}
                </div>
              ))}
            </div>
          }
        >
          <div className="-mx-1 px-2 rounded-full text-white bg-[#DCDFEC] flex items-center justify-center border-solid border bg border-[#f5f6f8] cursor-pointer">
            <span className="!font-poppins text-xs text-[#676879]">
              +{remainingPersonandTeams.length}
            </span>
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export default PersonColumn;
